import { useCallback, useState } from 'react';

import { message } from 'antd';

import { VALIDATION_RULES } from '@/shared/lib/constants';
import { getTextFromHtml, validateDescriptionLength } from '@/shared/lib/text';

type UseDescriptionValidationOptions = {
  minLength?: number;
  maxLength?: number;
  errorMessage?: string;
};

/**
 * Description 입력 필드의 실시간 validation을 관리하는 Hook
 *
 * @description
 * - 실시간으로 description 길이를 체크하고 에러 상태를 관리합니다
 * - Form의 onValuesChange와 함께 사용하여 즉각적인 피드백을 제공합니다
 * - 제출 전 최종 validation을 수행합니다
 */
export const useDescriptionValidation = (options: UseDescriptionValidationOptions = {}) => {
  const {
    minLength = VALIDATION_RULES.DESCRIPTION_MIN_LENGTH,
    maxLength = VALIDATION_RULES.DESCRIPTION_MAX_LENGTH,
    errorMessage = '설명 검증 실패',
  } = options;

  const [descriptionError, setDescriptionError] = useState(false);

  /**
   * Form의 onValuesChange 핸들러
   * description 필드가 변경될 때마다 실시간 validation 수행
   *
   * @description
   * - 입력이 시작되지 않았으면 에러 표시 안 함 (빈 문자열 허용)
   * - 입력이 시작된 경우에만 길이 검증 수행
   * - 더 나은 UX를 위해 사용자가 입력을 시도한 후에만 에러 표시
   */
  const handleValuesChange = useCallback(
    (changedValues: Record<string, unknown>) => {
      if ('description' in changedValues) {
        const description = changedValues.description as string | undefined;

        // 빈 문자열이나 undefined인 경우 에러 표시 안 함
        if (!description || getTextFromHtml(description).length === 0) {
          setDescriptionError(false);
        } else {
          // 입력이 시작된 경우에만 길이 검증
          setDescriptionError(!validateDescriptionLength(description, minLength, maxLength));
        }
      }
    },
    [minLength, maxLength]
  );

  /**
   * 제출 전 최종 validation 체크
   * @returns validation 통과 여부
   */
  const validateBeforeSubmit = useCallback(
    (description: string): boolean => {
      const isValid = validateDescriptionLength(description, minLength, maxLength);
      if (!isValid) {
        setDescriptionError(true);
        message.error(errorMessage);
      } else {
        setDescriptionError(false);
      }
      return isValid;
    },
    [minLength, maxLength, errorMessage]
  );

  /**
   * 에러 상태 초기화
   * 모달 닫기 시 호출
   */
  const resetError = useCallback(() => {
    setDescriptionError(false);
  }, []);

  return {
    descriptionError,
    handleValuesChange,
    validateBeforeSubmit,
    resetError,
  };
};
